// @flow
import React, {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {colors} from '../../assets/styles/colors';
import {
  Typography,
  Hidden,
  Menu,
  MenuItem,
  Fade,
  Box,
  Divider,
  Avatar,
  ButtonBase,
  Icon,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import HelpIcon from '@material-ui/icons/Help';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import {useAuth, useUser} from '../../modules/auth/utils';
import {TIENDADA_LINKS} from '../../constants';
import {useStoreInfo} from '../../modules/store/utils';
import {getTierName} from '../../modules/store/utils/utils_tier';
import CustomChip from '../CustomChip';
import {DateRange} from '@material-ui/icons';
import env from '../../environment';

const useStyles = makeStyles(theme => ({
  profileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: 0,
    '&:hover': {
      opacity: 0.8,
    },
  },
  profileIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: `${colors.purple6} !important`,
    border: `1px solid ${colors.grey100} !important`,
    borderRadius: '50%',
    boxShadow: '0 0 0 2px #BE9FFD',
    padding: 0,
  },
  textIcon: {
    color: `${colors.purple3} !important`,
    fontWeight: 500,
    fontSize: '.875rem',
  },
  icon: {
    margin: '3px',
  },
  menu: {
    maxWidth: 260,
    '& .MuiPaper-root': {
      borderRadius: 16,
      minWidth: 260,
      marginTop: theme.spacing(1),
      boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
    },
  },
  profileHeader: {
    padding: '.25rem .75rem',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    textDecoration: 'none',
    gap: '.5rem',
    '&:hover': {
      backgroundColor: colors.grey200,
    },

    [theme.breakpoints.up('sm')]: {
      padding: '.5rem',
    },

    [theme.breakpoints.down('md')]: {
      padding: '.5rem .75rem',
    },
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: colors.purple3,
    fontSize: '1rem',
  },
  profileInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  userName: {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: colors.grey900,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '22ch',
  },
  userEmail: {
    fontSize: '0.75rem',
    color: colors.grey600,
  },
  planBadge: {
    backgroundColor: colors.success,
    color: 'white',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  menuItem: {
    padding: '.25rem .75rem',
    gap: '.5rem',
    '&:hover': {
      backgroundColor: colors.grey50,
    },
    [theme.breakpoints.up('sm')]: {
      padding: '.5rem',
    },
  },
  menuIcon: {
    color: colors.grey700,
    fontSize: 20,
  },
  logoutItem: {
    color: colors.error,
    '& svg': {
      color: colors.error,
    },
  },
  divider: {
    margin: '.5rem 0',
  },
  version: {
    fontSize: '0.675rem',
    color: colors.grey600,
    textAlign: 'center',
  },
}));

export default function ProfileButton() {
  const user = useUser();
  const store = useStoreInfo();
  const classes = useStyles();
  const {logout} = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!user || !store) return null;

  const handleClick = (event: SyntheticEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const tierName = getTierName(store);
  const userInitials = `${user.firstName?.charAt(0) ||
    ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <Fragment>
      <ButtonBase
        edge="end"
        aria-label="account of current user"
        aria-controls="profile-menu"
        aria-haspopup="true"
        onClick={handleClick}
        className={classes.profileButton}
      >
        <Icon className={classes.profileIcon}>
          <PersonIcon color="primary" className={classes.icon} />
        </Icon>

        <Hidden mdDown>
          <Typography className={classes.textIcon}>
            {user.firstName +
              ' ' +
              (user.lastName ? user.lastName.charAt(0) : '') +
              '.'}
          </Typography>
        </Hidden>
      </ButtonBase>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        className={classes.menu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ButtonBase
          component={Link}
          className={classes.profileHeader}
          to="/profile"
          onClick={handleClose}
        >
          <Avatar className={classes.avatar}>{userInitials}</Avatar>
          <Box className={classes.profileInfo}>
            <Typography className={classes.userName}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography className={classes.userEmail}>{user.email}</Typography>
          </Box>
        </ButtonBase>

        <Divider className={classes.divider} />
        <MenuItem
          className={classes.menuItem}
          onClick={handleClose}
          component={Link}
          to="/tiers-list"
        >
          <DateRange className={classes.menuIcon} />
          <Typography variant="body2">Planes</Typography>
          <CustomChip label={tierName} size="small" color="success" />
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          component="a"
          href={TIENDADA_LINKS.youtube.main_channel}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
        >
          <PlayCircleFilledIcon className={classes.menuIcon} />
          <Typography variant="body2">Tutorial</Typography>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          component="a"
          href="https://api.whatsapp.com/send?phone=51972076020&text=Hola!%20Quisiera%20contactarme%20con%20TiendaDa."
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
        >
          <HelpIcon className={classes.menuIcon} />
          <Typography variant="body2">Ayuda</Typography>
        </MenuItem>

        <Divider className={classes.divider} />

        <MenuItem
          className={`${classes.menuItem} ${classes.logoutItem}`}
          onClick={handleLogout}
        >
          <ExitToAppIcon className={classes.menuIcon} />
          <Typography variant="body2">Cerrar sesión</Typography>
        </MenuItem>
        <Typography className={classes.version}>
          Versión {env.version}
        </Typography>
      </Menu>
    </Fragment>
  );
}
